/**
 * One line at the bottom of the screen confirming that something happened.
 *
 * `role="status"` rather than `role="alert"`: these are confirmations of things the person just
 * did, so they are announced politely instead of interrupting. Nothing destructive is ever
 * *reported* only by a toast — a failure that matters is rendered where the person is looking.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

const TOAST_MS = 3200;

const ToastContext = createContext<((message: string) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  // Bumped on every show, so two identical messages in a row still restart the timer rather than
  // the second one being swallowed as "no change".
  const [nonce, setNonce] = useState(0);

  const show = useCallback((next: string) => {
    setMessage(next);
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (message === null) return;
    const timer = setTimeout(() => {
      setMessage(null);
    }, TOAST_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [message, nonce]);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {message !== null && (
          <div className="toast" key={nonce}>
            {message}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (message: string) => void {
  const show = useContext(ToastContext);
  if (show === null) throw new Error("useToast must be used inside a ToastProvider");
  return show;
}
